// src/hooks/useDragScroll.ts
import { useCallback, useRef } from 'react';

// Minimum pointer movement (px) before a press counts as a drag rather
// than a click. Below this, the underlying row/button click fires as normal.
const DRAG_THRESHOLD = 6;

interface DragState {
  pointerId: number | null;
  dragging: boolean;
  startX: number;
  startScrollLeft: number;
}

// Adds mouse drag-to-pan to a horizontally overflowing element. Touch and
// pen pointers are left untouched since the browser already provides native
// momentum scrolling for those.
//
// Pointer capture is only requested once the drag threshold is actually
// crossed, not on every press. Capturing on every mousedown would redirect
// the resulting click to this element regardless of what was actually
// under the cursor, silently breaking buttons nested inside. Deferring it
// means a plain click never touches capture at all and dispatches to its
// real target normally; a confirmed drag still gets capture so movement
// keeps tracking even if the cursor leaves the row, and the trailing click
// lands back on this element where it can be swallowed.
export function useDragScroll<T extends HTMLElement = HTMLDivElement>() {
  const elRef = useRef<T | null>(null);
  const state = useRef<DragState>({
    pointerId: null,
    dragging: false,
    startX: 0,
    startScrollLeft: 0,
  });

  const swallowNextClick = useCallback((el: T) => {
    const handler = (event: MouseEvent) => event.stopPropagation();
    el.addEventListener('click', handler, { capture: true, once: true });
  }, []);

  const reset = useCallback((el: T) => {
    el.style.cursor = '';
    el.style.userSelect = '';
    state.current.pointerId = null;
    state.current.dragging = false;
  }, []);

  const handlePointerDown = useCallback((event: PointerEvent) => {
    const el = elRef.current;
    if (!el || event.pointerType !== 'mouse' || event.button !== 0) return;
    state.current = {
      pointerId: event.pointerId,
      dragging: false,
      startX: event.clientX,
      startScrollLeft: el.scrollLeft,
    };
  }, []);

  const handlePointerMove = useCallback((event: PointerEvent) => {
    const el = elRef.current;
    if (!el || event.pointerId !== state.current.pointerId) return;

    const delta = event.clientX - state.current.startX;

    if (!state.current.dragging) {
      if (Math.abs(delta) < DRAG_THRESHOLD) return;
      state.current.dragging = true;
      el.setPointerCapture(event.pointerId);
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
    }

    event.preventDefault();
    el.scrollLeft = state.current.startScrollLeft - delta;
  }, []);

  const handlePointerUp = useCallback(
    (event: PointerEvent) => {
      const el = elRef.current;
      if (!el || event.pointerId !== state.current.pointerId) return;

      if (el.hasPointerCapture(event.pointerId)) {
        el.releasePointerCapture(event.pointerId);
      }

      const wasDragging = state.current.dragging;
      reset(el);

      if (wasDragging) {
        swallowNextClick(el);
      }
    },
    [reset, swallowNextClick]
  );

  return useCallback(
    (el: T | null) => {
      const prev = elRef.current;
      if (prev) {
        prev.removeEventListener('pointerdown', handlePointerDown);
        prev.removeEventListener('pointermove', handlePointerMove);
        prev.removeEventListener('pointerup', handlePointerUp);
        prev.removeEventListener('pointercancel', handlePointerUp);
      }

      elRef.current = el;

      if (el) {
        el.addEventListener('pointerdown', handlePointerDown);
        el.addEventListener('pointermove', handlePointerMove);
        el.addEventListener('pointerup', handlePointerUp);
        el.addEventListener('pointercancel', handlePointerUp);
      }
    },
    [handlePointerDown, handlePointerMove, handlePointerUp]
  );
}
